import React, { useEffect } from 'react';
import { LoginEvent, QuestionBankAPILog, DeviceInfo, UserLocation } from '../types/usage';
import { Timestamp } from 'firebase/firestore';
import './stylesheets/RecordDetailsModal.css';

interface RecordDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  record: (LoginEvent & { id: string }) | (QuestionBankAPILog & { id: string }) | null;
  type: 'login' | 'api';
  getUserEmail?: (userId: string) => string;
  onDelete?: (record: (LoginEvent & { id: string }) | (QuestionBankAPILog & { id: string }), type: 'login' | 'api') => void;
}

const RecordDetailsModal: React.FC<RecordDetailsModalProps> = ({
  isOpen,
  onClose,
  record,
  type,
  getUserEmail,
  onDelete
}) => {
  useEffect(() => {
    if (isOpen) {
      // Store the current overflow value
      const originalOverflow = document.body.style.overflow;
      // Prevent scrolling
      document.body.style.overflow = 'hidden';
      
      // Cleanup function to restore original overflow
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  const handleDelete = () => {
    if (onDelete && record) {
      onDelete(record, type);
    }
  };

  if (!isOpen || !record) return null;

  const formatTimestamp = (timestamp: any): string => {
    if (timestamp instanceof Timestamp) {
      return timestamp.toDate().toLocaleString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZoneName: 'short'
      });
    }
    if (typeof timestamp === 'string') {
      return new Date(timestamp).toLocaleString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZoneName: 'short'
      });
    }
    return 'Unknown';
  };

  const renderDetailItem = (label: string, value: string, fullWidth = false) => (
    <div className={`detail-item ${fullWidth ? 'full-width' : ''}`}>
      <div className="detail-label">{label}:</div>
      <div className="detail-value">{value}</div>
    </div>
  );

  const renderLoginContent = (event: LoginEvent & { id: string }) => (
    <>
      <div className="detail-section">
        <h4>Login Information</h4>
        {renderDetailItem('Record ID', event.id, true)}
        {renderDetailItem('User ID', event.userId, true)}
        {renderDetailItem('User Email', getUserEmail ? getUserEmail(event.userId) : event.userId, true)}
        {renderDetailItem('Login Method', event.loginMethod)}
        {renderDetailItem('Timestamp', formatTimestamp(event.timestamp), true)}
      </div>

      <div className="detail-section">
        <h4>Device Information</h4>
        {renderDetailItem('Platform', event.deviceInfo.platform)}
        {renderDetailItem('Browser', event.deviceInfo.browser)}
        {renderDetailItem('Device Type', event.deviceInfo.isMobile ? 'Mobile' : 'Desktop')}
        {renderDetailItem('User Agent', event.deviceInfo.userAgent, true)}
      </div>

      <div className="detail-section">
        <h4>Location Information</h4>
        {renderDetailItem('Country', event.location.country)}
        {renderDetailItem('Timezone', event.location.timezone)}
        {event.location.ip && renderDetailItem('IP Address', event.location.ip)}
        {event.location.type && renderDetailItem('Location Type', event.location.type)}
      </div>
    </>
  );

  const renderAPIContent = (log: QuestionBankAPILog & { id: string }) => (
    <>
      <div className="detail-section">
        <h4>API Request Information</h4>
        {renderDetailItem('Record ID', log.id, true)}
        {renderDetailItem('User ID', log.userId, true)}
        {renderDetailItem('User Email', log.userEmail, true)}
        {renderDetailItem('Request Type', log.requestType)}
        {renderDetailItem('Specialty', log.specialty.replace('-', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()))}
        {renderDetailItem('Timestamp', formatTimestamp(log.timestamp), true)}
      </div>

      <div className="detail-section">
        <h4>Response Information</h4>
        {renderDetailItem('Status Code', `${log.status} ${log.status === 200 ? '(Success)' : '(Error)'}`)}
        {renderDetailItem('Response Time', `${log.response.responseTimeMs} ms`)}
        {renderDetailItem('Has Data', log.response.hasData ? 'Yes' : 'No')}
        {renderDetailItem('Total Count', log.response.totalCount.toString())}
        {log.response.error && renderDetailItem('Error', log.response.error, true)}
      </div>

      <div className="detail-section">
        <h4>Device Information</h4>
        {renderDetailItem('Platform', log.deviceInfo.platform)}
        {renderDetailItem('Browser', log.deviceInfo.browser)}
        {renderDetailItem('Device Type', log.deviceInfo.isMobile ? 'Mobile' : 'Desktop')}
        {renderDetailItem('User Agent', log.deviceInfo.userAgent, true)}
      </div>

      <div className="detail-section">
        <h4>Location Information</h4>
        {renderDetailItem('Country', log.userLocation.country)}
        {renderDetailItem('Timezone', log.userLocation.timezone)}
        {log.userLocation.ip && renderDetailItem('IP Address', log.userLocation.ip)}
        {log.userLocation.type && renderDetailItem('Location Type', log.userLocation.type)}
      </div>
    </>
  );

  return (
    <div className="record-modal-overlay" onClick={onClose}>
      <div className="record-modal" onClick={(e) => e.stopPropagation()}>
        <div className="record-modal-header">
          <h3>{type === 'login' ? 'Login Event Details' : 'API Log Details'}</h3>
          <button className="record-modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="record-modal-body">
          {type === 'login' 
            ? renderLoginContent(record as LoginEvent & { id: string })
            : renderAPIContent(record as QuestionBankAPILog & { id: string })
          }
        </div>
        
        <div className="record-modal-footer">
          {onDelete && (
            <button 
              className="record-modal-delete-btn" 
              onClick={handleDelete}
              style={{
                background: '#dc3545',
                color: 'white',
                border: 'none',
                padding: '10px 16px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                marginRight: '12px',
                transition: 'background-color 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#c82333';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#dc3545';
              }}
            >
              🗑️ Delete Record
            </button>
          )}
          <button className="record-modal-close-btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default RecordDetailsModal;